import {
    domainColumns,
    formatOutOf,
    formatYesNo,
    getBoolean,
    getNumber,
    getScale,
    getScales,
    getString,
    mapRiskLabel,
    mapScreeningLabel,
    type OutcomeTableColumn,
    type OutcomeTableConfig,
} from '../helpers';

export const dass21Config: OutcomeTableConfig = {
    title: 'Resultados DASS-21',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const depression = getScale(scales, 'depression');
        const anxiety = getScale(scales, 'anxiety');
        const stress = getScale(scales, 'stress');

        return [
            {
                domain: 'Depressão',
                result: formatOutOf(depression.raw, 42),
                interpretation: getString(depression.severity_label),
            },
            {
                domain: 'Ansiedade',
                result: formatOutOf(anxiety.raw, 42),
                interpretation: getString(anxiety.severity_label),
            },
            {
                domain: 'Estresse',
                result: formatOutOf(stress.raw, 42),
                interpretation: getString(stress.severity_label),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return (
            'severity_label' in getScale(scales, 'depression') &&
            'severity_label' in getScale(scales, 'anxiety') &&
            'severity_label' in getScale(scales, 'stress')
        );
    },
};

const formatEndorsedCount = (value: unknown) => {
    const count = getNumber(value);
    return count === null ? null : `${count} item(ns) endossado(s)`;
};

export const pcl5Config: OutcomeTableConfig = {
    title: 'Resultados PCL-5',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        const clusterB = getScale(scales, 'cluster_b_intrusion');
        const clusterC = getScale(scales, 'cluster_c_avoidance');
        const clusterD = getScale(scales, 'cluster_d_cognition_mood');
        const clusterE = getScale(scales, 'cluster_e_arousal_reactivity');
        const provisional = getScale(scales, 'provisional_diagnosis');
        const criteriaMet = getBoolean(provisional.criteria_met);

        return [
            {
                domain: 'Sintomas de TEPT (total PCL-5)',
                result: formatOutOf(total.raw, 80),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
            {
                domain: 'Cluster B - Intrusão (itens 1-5)',
                result: formatOutOf(clusterB.raw, 20),
                interpretation: formatEndorsedCount(clusterB.endorsed_count),
            },
            {
                domain: 'Cluster C - Evitação (itens 6-7)',
                result: formatOutOf(clusterC.raw, 8),
                interpretation: formatEndorsedCount(clusterC.endorsed_count),
            },
            {
                domain: 'Cluster D - Cognição/Humor (itens 8-14)',
                result: formatOutOf(clusterD.raw, 28),
                interpretation: formatEndorsedCount(clusterD.endorsed_count),
            },
            {
                domain: 'Cluster E - Excitabilidade (itens 15-20)',
                result: formatOutOf(clusterE.raw, 24),
                interpretation: formatEndorsedCount(clusterE.endorsed_count),
            },
            {
                domain: 'Critérios DSM-5 (diagnóstico provisório)',
                result: formatYesNo(criteriaMet),
                interpretation:
                    criteriaMet === null
                        ? null
                        : criteriaMet
                          ? 'Padrão de sintomas compatível com TEPT provável (avaliar clinicamente)'
                          : 'Padrão de sintomas não preenche os critérios DSM-5',
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return 'criteria_met' in getScale(scales, 'provisional_diagnosis');
    },
};

const snapivColumns: OutcomeTableColumn[] = [
    { key: 'factor', label: 'Subescala' },
    { key: 'score', label: 'Sintomas presentes' },
    { key: 'mean', label: 'Média (0-3)' },
    { key: 'classification', label: 'Rastreio' },
];

export const snapivConfig: OutcomeTableConfig = {
    title: 'Resultados SNAP-IV',
    columns: snapivColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const inattention = getScale(scales, 'inattention');
        const hyperactivity = getScale(scales, 'hyperactivity_impulsivity');
        const oppositional = getScale(scales, 'oppositional_defiant');

        return [
            {
                factor: 'Desatenção',
                score: formatOutOf(inattention.symptoms_present, 9),
                mean: getNumber(inattention.mean),
                classification: mapScreeningLabel(inattention.screening_positive),
            },
            {
                factor: 'Hiperatividade/Impulsividade',
                score: formatOutOf(hyperactivity.symptoms_present, 9),
                mean: getNumber(hyperactivity.mean),
                classification: mapScreeningLabel(hyperactivity.screening_positive),
            },
            {
                factor: 'Transtorno Opositor-Desafiador',
                score: formatOutOf(oppositional.symptoms_present, 8),
                mean: getNumber(oppositional.mean),
                classification: mapScreeningLabel(oppositional.screening_positive),
            },
            {
                factor: 'Respondente',
                score: getString(outcomes.respondent),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return (
            'symptoms_present' in getScale(scales, 'inattention') &&
            'symptoms_present' in getScale(scales, 'oppositional_defiant')
        );
    },
};

const coreomColumns: OutcomeTableColumn[] = [
    { key: 'domain', label: 'Dimensão' },
    { key: 'score', label: 'Escore clínico (0-40)' },
    { key: 'interpretation', label: 'Interpretação' },
];

export const coreomConfig: OutcomeTableConfig = {
    title: 'Resultados CORE-OM',
    columns: coreomColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const wellbeing = getScale(scales, 'wellbeing');
        const problems = getScale(scales, 'problems');
        const functioning = getScale(scales, 'functioning');
        const risk = getScale(scales, 'risk');
        const total = getScale(scales, 'total');
        const totalNonRisk = getScale(scales, 'total_non_risk');

        return [
            {
                domain: 'Bem-estar subjetivo',
                score: getNumber(wellbeing.clinical_score),
            },
            {
                domain: 'Problemas/Sintomas',
                score: getNumber(problems.clinical_score),
            },
            {
                domain: 'Funcionamento',
                score: getNumber(functioning.clinical_score),
            },
            {
                domain: 'Risco',
                score: getNumber(risk.clinical_score),
                interpretation: mapRiskLabel(risk.any_item_endorsed),
            },
            {
                domain: 'Total (corte clínico >= 10)',
                score: getNumber(total.clinical_score),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
            {
                domain: 'Total sem itens de risco',
                score: getNumber(totalNonRisk.clinical_score),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return (
            'clinical_score' in getScale(scales, 'total') &&
            'any_item_endorsed' in getScale(scales, 'risk')
        );
    },
};

const apsrColumns: OutcomeTableColumn[] = [
    { key: 'scale', label: 'Subescala' },
    { key: 'score', label: 'Pontuação' },
    { key: 'mean', label: 'Média (1-7)' },
];

export const apsrConfig: OutcomeTableConfig = {
    title: 'Resultados APS-R',
    columns: apsrColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const standards = getScale(scales, 'standards');
        const order = getScale(scales, 'order');
        const discrepancy = getScale(scales, 'discrepancy');

        return [
            {
                scale: 'Padrões',
                score: formatOutOf(standards.raw, 49),
                mean: getNumber(standards.mean),
            },
            {
                scale: 'Ordem',
                score: formatOutOf(order.raw, 28),
                mean: getNumber(order.mean),
            },
            {
                scale: 'Discrepância (perfeccionismo mal-adaptativo)',
                score: formatOutOf(discrepancy.raw, 84),
                mean: getNumber(discrepancy.mean),
            },
        ];
    },
    match: (outcomes) => {
        const scaleKeys = Object.keys(getScales(outcomes));
        return (
            scaleKeys.includes('standards') &&
            scaleKeys.includes('order') &&
            scaleKeys.includes('discrepancy')
        );
    },
};
